import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import OtpInput from "react-otp-input";
import { BsPersonFillAdd } from "react-icons/bs";
import { FaArrowLeft } from "react-icons/fa";
import Handler from "../../../Helpers/Handler";
import { Skeleton } from "../../../Components/Needed";
import { listBackPath } from "../../../Helpers/useListParams";

const EMPTY_FORM = { firstName: "", lastName: "", phone: "", gender: "", photo: null };

const AssociateFormPage = () => {
    const { associateId } = useParams();
    const navigate = useNavigate();
    const mode = associateId ? "edit" : "create";

    const [form, setForm] = useState(EMPTY_FORM);
    const [preview, setPreview] = useState(null);
    const [otpView, setOtpView] = useState(false);
    const [otp, setOtp] = useState("");
    const [loading, setLoading] = useState(mode === "edit");
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        if (mode !== "edit") return;
        setLoading(true);
        const response = await Handler({ method: "GET", url: `/admin/client/associate/details/${associateId}` });
        if (response?.success) {
            const a = response.data.data;
            setForm({ firstName: a?.firstName || "", lastName: a?.lastName || "", phone: a?.phone || "", gender: a?.gender || "", photo: null });
            setPreview(a?.photo || null);
        } else {
            toast.error("Associate not found");
            navigate(listBackPath("/associates"));
        }
        setLoading(false);
    }, [associateId, mode, navigate]);

    useEffect(() => { load(); }, [load]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setPreview(URL.createObjectURL(file));
            setForm((prev) => ({ ...prev, photo: file }));
        }
    };

    const validateForm = () => {
        const { firstName, lastName, phone, gender, photo } = form;
        if (!firstName.trim() || !lastName.trim() || !gender || (mode === "create" && (!phone.trim() || !photo))) {
            toast.error("Please fill all required fields");
            return false;
        }
        if (mode === "create" && !/^[6-9]\d{9}$/.test(phone)) {
            toast.error("Enter valid 10 digit phone number");
            return false;
        }
        return true;
    };

    const sendOtp = async () => {
        if (!validateForm()) return;
        setSaving(true);
        const response = await Handler({ method: "post", url: "/admin/client/associate/registration/phone-otp", data: { phone: form.phone } });
        if (response?.success) {
            setOtpView(true);
            toast.success("OTP sent successfully");
        } else {
            toast.error(response?.data?.message || "Failed to send OTP");
        }
        setSaving(false);
    };

    const registerAssociate = async () => {
        if (otp.length !== 6) {
            toast.error("Enter valid 6 digit OTP");
            return;
        }
        setSaving(true);
        const formData = new FormData();
        formData.append("firstName", form.firstName);
        formData.append("lastName", form.lastName);
        formData.append("phone", form.phone);
        formData.append("gender", form.gender);
        formData.append("otp", otp);
        formData.append("photo", form.photo);
        const response = await Handler({ method: "post", url: "/admin/client/associate/registration", data: formData, formdata: true });
        if (response?.success) {
            toast.success(response?.data?.message || "Associate registered successfully");
            navigate(`/associates/${response?.data?.data?.associateId}`);
        } else {
            toast.error(response?.data?.message || "Registration failed");
            setSaving(false);
        }
    };

    const updateProfile = async () => {
        if (!validateForm()) return;
        setSaving(true);
        const formData = new FormData();
        formData.append("firstName", form.firstName);
        formData.append("lastName", form.lastName);
        formData.append("gender", form.gender);
        if (form.photo) formData.append("photo", form.photo);
        const response = await Handler({ method: "patch", url: `/admin/client/associate/profile/${associateId}`, data: formData, formdata: true });
        if (response?.success) {
            toast.success(response?.data?.message || "Profile updated successfully");
            navigate(`/associates/${associateId}`);
        } else {
            toast.error(response?.data?.message || "Unable to update profile");
        }
        setSaving(false);
    };

    if (loading) {
        return (
            <div className="settings-card">
                <Skeleton height={24} width="30%" className="mb-3" />
                <Skeleton height={220} />
            </div>
        );
    }

    return (
        <div className="container mt-4">
            <button type="button" className="btn btn-link text-decoration-none px-3 mb-3 d-inline-flex align-items-center gap-2" onClick={() => navigate(mode === "edit" ? `/associates/${associateId}` : "/associates")}>
                <FaArrowLeft /> {mode === "edit" ? "Back to associate" : "Back to associates"}
            </button>

            <div className="row justify-content-center">
                <div className="col-lg-8 col-md-10">
                    <div className="card shadow-sm border-0 rounded-4">
                        <div className="card-body p-4">
                            <div className="d-flex align-items-center mb-4">
                                <div className="d-flex justify-content-center align-items-center me-2 rounded-3 bg-primary text-white p-2 fs-4">
                                    <BsPersonFillAdd />
                                </div>
                                <h4 className="m-0 fw-semibold">{mode === "create" ? "Associate Registration" : "Edit Associate"}</h4>
                            </div>

                            {mode === "create" && otpView ? (
                                <div className="my-5">
                                    <div className="my-5 d-flex flex-column justify-content-center align-items-center">
                                        <label className="form-label fw-semibold mb-4">Enter OTP</label>
                                        <OtpInput
                                            value={otp}
                                            onChange={setOtp}
                                            numInputs={6}
                                            renderSeparator={<span className="mx-1"></span>}
                                            renderInput={(props) => (
                                                <input {...props} disabled={saving} className="form-control text-center mx-1" style={{ width: "45px", height: "45px" }} />
                                            )}
                                        />
                                    </div>
                                    <div className="text-center mt-4">
                                        <button className="btn btn-primary px-4" onClick={registerAssociate} disabled={saving}>
                                            {saving ? "Registering..." : "Register"}
                                        </button>
                                    </div>
                                    <div className="text-center mt-3">
                                        <button type="button" className="btn btn-link" disabled={saving} onClick={() => { setOtpView(false); setOtp(""); }}>Change details</button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="row">
                                        <div className="col-12 mb-4 text-center">
                                            <div className="position-relative d-inline-block">
                                                {preview ? (
                                                    <img src={preview} alt="Preview" className="rounded-circle" width="120" height="120" style={{ objectFit: "cover" }} />
                                                ) : (
                                                    <div className="rounded-circle bg-light d-flex align-items-center justify-content-center border" style={{ width: 120, height: 120 }}>Upload</div>
                                                )}
                                                <input disabled={saving} type="file" accept="image/*" className="position-absolute top-0 start-0 w-100 cursor-pointer h-100 opacity-0" onChange={handleImageChange} />
                                            </div>
                                            <div className="mt-2">Upload Photo</div>
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">First Name</label>
                                            <input type="text" name="firstName" autoComplete="off" value={form.firstName} disabled={saving} onChange={handleChange} className="form-control" placeholder="Enter first name" />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Last Name</label>
                                            <input type="text" name="lastName" autoComplete="off" value={form.lastName} disabled={saving} onChange={handleChange} className="form-control" placeholder="Enter last name" />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Phone No</label>
                                            <input
                                                type="text"
                                                name="phone"
                                                value={form.phone}
                                                autoComplete="off"
                                                disabled={saving || mode === "edit"}
                                                onChange={(e) => (e.target.value.length <= 10 && Number(e.target.value) == e.target.value) ? handleChange(e) : ""}
                                                className="form-control"
                                                placeholder="Enter phone number"
                                            />
                                            {mode === "edit" ? <div className="form-text">Phone can't be changed after registration.</div> : ""}
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Gender</label>
                                            <select name="gender" value={form.gender} disabled={saving} onChange={handleChange} className="form-select">
                                                <option value="">Select Gender</option>
                                                <option value="male">Male</option>
                                                <option value="female">Female</option>
                                                <option value="others">Other</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="text-center mt-4">
                                        <button className="btn btn-primary px-4" onClick={mode === "create" ? sendOtp : updateProfile} disabled={saving}>
                                            {saving ? (mode === "create" ? "Sending..." : "Saving...") : (mode === "create" ? "Send OTP" : "Save Changes")}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AssociateFormPage;
